import { ChangeDetectionStrategy, Component, input } from '@angular/core';

export type ButtonVariant =
  'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-outline' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonType = 'button' | 'submit' | 'reset';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-600 text-text-on-brand hover:bg-brand-700 active:bg-brand-800 focus-visible:outline-brand-600',
  secondary:
    'bg-surface-base text-text-primary border border-border-default hover:bg-surface-sunken focus-visible:outline-border-strong',
  ghost:
    'bg-transparent text-text-primary hover:bg-surface-sunken focus-visible:outline-border-strong',
  danger:
    'bg-status-danger text-text-on-brand hover:opacity-90 focus-visible:outline-status-danger',
  'danger-outline':
    'bg-surface-base text-status-danger border border-status-danger/30 hover:bg-status-danger/5 focus-visible:outline-status-danger',
  success:
    'bg-status-success text-text-on-brand hover:bg-status-success/90 active:bg-status-success focus-visible:outline-status-success',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm rounded-md gap-1.5',
  md: 'h-11 px-4 text-sm rounded-md gap-2',
  lg: 'h-12 px-5 text-base rounded-lg gap-2',
};

@Component({
  selector: 'g-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      [type]="type()"
      [disabled]="disabled() || loading()"
      [class]="
        'inline-flex items-center justify-center font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 ' +
        sizeClass() +
        ' ' +
        variantClass()
      "
    >
      @if (loading()) {
        <span class="g-spinner" aria-hidden="true"></span>
      }
      <ng-content />
    </button>
  `,
  styles: [
    `
      .g-spinner {
        width: 1em;
        height: 1em;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 9999px;
        animation: g-spin 0.75s linear infinite;
      }
      @keyframes g-spin {
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
})
export class ButtonComponent {
  readonly variant = input<ButtonVariant>('primary');
  readonly size = input<ButtonSize>('md');
  readonly type = input<ButtonType>('button');
  readonly disabled = input<boolean>(false);
  readonly loading = input<boolean>(false);

  protected variantClass(): string {
    return VARIANT_CLASSES[this.variant()];
  }
  protected sizeClass(): string {
    return SIZE_CLASSES[this.size()];
  }
}
